import React, { useImperativeHandle, forwardRef } from "react"

import ResizableElementWrapper from "../shared/ResizableElementWrapper.component"
import ChartElementVariableSelector from "./ChartElementVariableSelector.component"
import ChartElementDisplayArea from "./ChartElementDisplayArea.component"
import { useChartElementContextMenu } from "./ChartElementContextMenu.component"
import { useChartElement } from "features/Reporting/hooks/Elements/useChartElement"

import {
  DEFAULT_CHART_WIDTH,
  DEFAULT_CHART_HEIGHT,
} from "constants/dashboardElementSizes"

import {
  SlideElement,
  DataElementConfig,
} from "types/ReportingDashboard/elements.types"
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import { ElementRef } from "../../ReportingDisplay.component"

/**
 * Element update payload for chart elements
 */
interface ChartElementUpdate {
  width?: number
  height?: number
}

/**
 * Props for ChartElement component
 */
export interface ChartElementProps {
  /** Global filter string passed to data elements */
  filter?: string
  /** Element object to render */
  element: SlideElement
  /** Data structure for variable selection */
  codebook: CodebookData
  /** Instance ID for engine queries */
  queryInstance: QueryInstance | null
  /** Callback when element properties change */
  onUpdate: (elementId: string, updates: ChartElementUpdate) => void
  /** Configuration for chart element */
  config?: DataElementConfig | null
  /** Callback for config updates */
  onConfigUpdate: (config: DataElementConfig | null) => void
  /** Whether in dashboard mode */
  isDashboard?: boolean
  /** Callback when element deletion is requested */
  onDeleteRequest?: () => void
  /** Sequential execution request handler */
  onElementExecutionRequest?: (
    elementId: string,
    executeFunction: () => Promise<void>,
    reason?: string
  ) => void
  /** Element registration handler */
  onRegisterElementRef?: (elementId: string, ref: ElementRef | null) => void
}

/**
 * Chart Element Component (Refactored with Business Logic Hook)
 *
 * A refactored chart component using:
 * - useChartElement: Custom hook containing all business logic
 * - ChartVariableSelector: Handles variable selection UI
 * - ChartDisplayArea: Handles chart rendering and display states
 * - ChartContextMenu: Handles context menu and submenus
 *
 * This demonstrates Phase 2 decomposition approach with extracted business logic.
 */
const ChartElement = forwardRef<ElementRef, ChartElementProps>((props, ref) => {
  // Use the custom hook to get all business logic and state
  const chartLogic = useChartElement(props)

  // Destructure what we need from the hook
  const {
    // State
    chartData,
    showSelects,
    isRunningScript,
    isQueuedForExecution,
    error,
    hasAttemptedLoad,
    convertedChartData,
    isSimpleData,
    selectOptions,

    // Config values
    selectedVariable,
    breakdownVariable,
    selectedStatisticType,
    chartType,
    showTotals,
    selectedDashboardKeys,
    selectedDashboardCategories,
    tableOperationType,

    // Hooks
    configHook,
    operatorManagement,

    // Handlers
    handleRefresh,
    handleEdit,
    handleDelete,
    handleToggleTotals,
    handleChartTypeChange,
    handleVariableToggle,
    handleCategoryToggle,
    handleVariableSelect,
    requestSequentialExecution,

    // Imperative handle values
    hasDataAndShouldRefresh,
  } = chartLogic

  // Expose methods via useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      hasDataAndShouldRefresh,
      requestSequentialExecution,
    }),
    [hasDataAndShouldRefresh, requestSequentialExecution]
  )

  // Destructure props
  const { element, queryInstance, isDashboard, onUpdate, onDeleteRequest } =
    props

  // Use context menu hook directly
  const { handleContextMenu, ContextMenus } = useChartElementContextMenu({
    hasChartData: !!chartData,
    showSelects,
    isLoading: isRunningScript,
    isQueuedForExecution,
    isDashboard: isDashboard || false,
    convertedChartData,
    chartType,
    showTotals,
    isSimpleData,
    selectedDashboardKeys,
    selectedDashboardCategories,
    canDelete: !!onDeleteRequest,
    onRefresh: handleRefresh,
    onEdit: handleEdit,
    onDelete: handleDelete,
    onToggleTotals: handleToggleTotals,
    onChartTypeChange: handleChartTypeChange,
    onVariableToggle: handleVariableToggle,
    onCategoryToggle: handleCategoryToggle,
  })

  // Styling
  const wrapperAdditionalClasses = `relative bg-white bg-opacity-90 rounded-md shadow-sm hover:shadow-md transition-shadow ${
    showSelects ? "inline-block" : ""
  }`

  const wrapperStyleOverrides: React.CSSProperties = {
    minWidth: "280px",
    minHeight: showSelects ? "auto" : "250px",
    overflow: "hidden",
    borderRadius: "4px",
    boxShadow:
      chartData && !showSelects ? "0 4px 8px rgba(0, 0, 0, 0.1)" : "none",
    ...(element.y >= 810
      ? {
          backgroundColor: "white",
          border: "1px solid #e2e8f0",
          margin: "0 auto",
        }
      : {}),
    ...(showSelects ? { width: "350px", height: "auto" } : {}),
  }

  const contentContainerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    padding: showSelects ? "0" : `0.5rem`,
    display: "flex",
    flexDirection: "column",
    position: "relative",
  }

  const chartHandlesConfig = {
    horizontal: true,
    vertical: true,
    corner: true,
  }

  return (
    <ResizableElementWrapper
      element={element}
      onUpdate={onUpdate}
      readonly={showSelects}
      minDimensions={{ width: 400, height: 250 }}
      defaultWidth={DEFAULT_CHART_WIDTH}
      defaultHeight={DEFAULT_CHART_HEIGHT}
      setSkipUpdateFlag={() => configHook.setSkipNextConfigUpdate(true)}
      additionalClasses={wrapperAdditionalClasses}
      styleOverrides={wrapperStyleOverrides}
      handleStyleType="visible"
      handlesConfiguration={chartHandlesConfig}
    >
      <div
        data-element-id={element.id}
        style={contentContainerStyle}
        onContextMenu={handleContextMenu}
      >
        {/* Variable selection */}
        {showSelects && (
          <ChartElementVariableSelector
            selectOptions={selectOptions}
            selectedVariable={selectedVariable}
            breakdownVariable={breakdownVariable}
            selectedStatisticType={selectedStatisticType}
            chartType={chartType}
            tableOperationType={tableOperationType}
            showTotals={showTotals}
            hasQueryInstance={!!queryInstance}
            isLoading={isRunningScript}
            isQueuedForExecution={isQueuedForExecution}
            elementId={element.id}
            operatorManagement={operatorManagement}
            onVariableSelect={handleVariableSelect}
            onChartTypeChange={handleChartTypeChange}
            onShowTotalsChange={(value: boolean) =>
              configHook.setConfigValue("showTotals", value)
            }
            onRun={() => requestSequentialExecution("user_run")}
            setSkipNextConfigUpdate={configHook.setSkipNextConfigUpdate}
          />
        )}
        {/* Chart display area */}
        <ChartElementDisplayArea
          hasChartData={!!chartData}
          showSelects={showSelects}
          isLoading={isRunningScript}
          isQueuedForExecution={isQueuedForExecution}
          error={error}
          isInitializing={configHook.isInitializing}
          hasAttemptedLoad={hasAttemptedLoad}
          convertedChartData={convertedChartData}
          selectedVariable={selectedVariable}
          isDashboard={isDashboard || false}
          selectedDashboardKeys={selectedDashboardKeys}
          selectedDashboardCategories={selectedDashboardCategories}
          chartType={chartType}
          showTotals={showTotals}
          elementHeight={element.height}
          onRefresh={handleRefresh}
          onSetShowTotals={(value: boolean) =>
            configHook.setConfigValue("showTotals", value)
          }
        />
      </div>

      {/* Context menu positioned outside the content container */}
      {ContextMenus}
    </ResizableElementWrapper>
  )
})

ChartElement.displayName = "ChartElement"

export default ChartElement
